import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import images from "../../assets/images";
import TemplateLayout from "../../components/layouts/templatelayout";
import Loader from "../../components/loader";
import {
  useBuyProductMutation,
  useGetPurchasedProductQuery,
} from "../../redux/Apis/DigitalProducts";
import { useGetCurrentSubscriptionQuery } from "../../redux/Apis/Packages";
import { useGetTemplateByIdQuery } from "../../redux/Apis/Template";
import { convertPrice, getConfigByCountryCode } from "../../utils/localization";
import { useTranslate } from "../../context/TranslationContext";

const getEmbedUrl = (url) => {
  if (!url) return null;
  const isPlaylist = url.includes("list=");
  const playlistMatch = url.match(/[?&]list=([a-zA-Z0-9_-]+)/);
  const videoMatch = url.match(/(?:v=|\.be\/|\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (isPlaylist && playlistMatch) {
    return `https://www.youtube.com/embed/videoseries?list=${playlistMatch[1]}`;
  }
  if (videoMatch) {
    return `https://www.youtube.com/embed/${videoMatch[1]}`;
  }
  return null;
};

const TemplateDetail = () => {
  const { t } = useTranslate("templateDetail");
  const lang = useSelector((state) => state.language.lang);
  const navigate = useNavigate();
  const { isLoggedIn, user } = useSelector((state) => state.authSlice);
  const { slug } = useParams();
  const currentUrl = `${window.location.origin}/templates/${slug}`;
  const countryCode =
    user?.country || localStorage?.getItem("selectedCountry") || "gb";
  const [displayPrice, setDisplayPrice] = useState(null);

  const { data, isLoading, refetch } = useGetTemplateByIdQuery({
    id: slug,
    params: { lang },
  });
  const template = data?.data;

  const { data: current_subscription, isSuccess: currentSuccess } =
    useGetCurrentSubscriptionQuery({ lang }, { skip: !isLoggedIn });

  const { data: purchasedProductsRes, isSuccess: hasPurchasedData } =
    useGetPurchasedProductQuery({ params: { lang } }, { skip: !isLoggedIn });

  const purchasedProducts = purchasedProductsRes?.data || [];

  const isProductPurchased =
    hasPurchasedData &&
    purchasedProducts.some((item) => item?.product?._id === template?._id);

  const [
    buyDigitalProduct,
    { data: buyData, isSuccess: buySuccess, error: buyError },
  ] = useBuyProductMutation();

  useEffect(() => {
    async function updatePrice() {
      if (template?.pricing) {
        const baseAmount = template.pricing;
        const { currency, symbol } = getConfigByCountryCode(countryCode);
        const converted = await convertPrice(baseAmount, "GBP", currency);
        setDisplayPrice(`${symbol} ${converted.toFixed(2)}`);
      }
    }
    updatePrice();
  }, [template?.pricing, countryCode]);

  useEffect(() => {
    if (slug) refetch();
  }, [slug, refetch]);

  useEffect(() => {
    if (buySuccess && buyData?.data?.checkoutUrl) {
      window.location.href = buyData.data.checkoutUrl;
    }
  }, [buySuccess, buyData]);

  useEffect(() => {
    if (buyError) {
      toast.error(buyError?.data?.message || t("purchaseError"));
    }
  }, [buyError, t]);

  if (isLoading) return <Loader loading={true} />;

  if (!template) {
    return (
      <TemplateLayout>
        <section className="text-center py-5">
          <h3 className="text-danger">{t("notFound")}</h3>
        </section>
      </TemplateLayout>
    );
  }

  const handleAccessClick = () => {
    // const hasMembership =
    //   currentSuccess && current_subscription?.data?.status === "active";
    const formatMembershipName = (key) => {
      if (!key) return "";
      const words = key
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1));
      return words.join(" ");
    };

    // Validate if user has correct membership
    const checkMembershipAccess = (current_subscription, requiredKey) => {
      if (!current_subscription?.data) return { allowed: false };

      const userPackage = current_subscription.data.package;
      const userKey = userPackage.title?.toLowerCase().replace(/\s+/g, "_");
      const required = requiredKey?.toLowerCase().replace(/\s+/g, "_");

      const isActive = current_subscription.data.status === "active";

      // Any active membership allowed
      if (required === "membership_only") {
        return { allowed: isActive };
      }

      // Specific membership required (fit_pro, fit_business)
      if (required !== userKey) {
        return {
          allowed: false,
          requiredReadable: formatMembershipName(required),
          currentReadable: userPackage.title,
        };
      }

      return { allowed: true };
    };

    if (!user?.role) {
      if (template.access_type === "free") {
        return toast.info(t("loginFree"));
      } else if (template.access_type === "membership") {
        return toast.info(t("loginMembership"));
      } else if (template.access_type === "paid") {
        return toast.info(t("loginPaid"));
      } else if (template.access_type === "custom") {
        return toast.info(t("loginCustom"));
      }
      return;
    }

    if (user.role === "learner") {
      if (template.access_type === "free") {
        return toast.info(t("switchCreatorFree"));
      } else if (template.access_type === "membership") {
        return toast.info(t("switchCreatorMembership"));
      } else if (template.access_type === "paid") {
        return toast.info(t("switchCreatorPaid"));
      } else if (template.access_type === "custom") {
        return toast.info(t("switchCreatorCustom"));
      }
      return;
    }

    if (template.access_type === "free") {
      return navigate("/my-teaching", {
        state: {
          activeTab: "pills-pss",
          access_type: template.access_type,
          template_id: template._id,
          title: template.title,
          template_price: template.pricing,
        },
      });
    }

    if (template.access_type === "membership") {
      const hasMembershipActive =
        currentSuccess && current_subscription?.data?.status === "active";

      if (!hasMembershipActive) {
        return toast.info(t("membershipUpgradeNeeded"));
      }

      const result = checkMembershipAccess(
        current_subscription,
        data?.data?.membership_type
      );

      if (!result.allowed) {
        const htmlMessage = t("membershipPlanNotAllowed")
          .replace("{{required}}", `<b>${result.requiredReadable}</b>`)
          .replace("{{current}}", `<b>${result.currentReadable}</b>`);

        return toast.info(
          <div dangerouslySetInnerHTML={{ __html: htmlMessage }} />
        );
      }
      // if (hasMembership) {
      return navigate("/my-teaching", {
        state: {
          activeTab: "pills-pss",
          access_type: template.access_type,
          template_id: template._id,
          title: template.title,
          template_price: template.pricing,
        },
      });
      // }
      return toast.info(t("upgradeMembership"));
    }

    if (template.access_type === "paid") {
      return buyDigitalProduct({ id: template._id, params: { lang } });
    }

    if (template.access_type === "custom") {
      return toast.success(t("customThanks"));
    }
  };

  const yoututbeUrl = getEmbedUrl(template?.youtube_link);

  return (
    <TemplateLayout>
      <section
        style={{
          backgroundColor: "#000",
          color: "#fff",
          padding: "60px 0",
          minHeight: "100vh",
        }}
      >
        <div className="container">
          {/* Header */}
          <div className="row align-items-center mb-5">
            <div className="col-md-6">
              <h1 className="fw-bold" style={{ fontSize: "40px" }}>
                {template.title}
              </h1>
              <p
                className="fs-5 mb-3"
                dangerouslySetInnerHTML={{ __html: template.subtitle || "" }}
              />
              <div className="fs-6 text-muted d-flex gap-2">
                <p className="mb-0 fw-bold">{t("production")}</p>
                <p
                  className="mb-0"
                  dangerouslySetInnerHTML={{
                    __html: template.prerequisites || "",
                  }}
                />
              </div>
              <div className="fs-6 text-muted d-flex gap-2">
                <p className="fw-bold">{t("qualifications")}</p>
                <p
                  dangerouslySetInnerHTML={{
                    __html: template.level || "",
                  }}
                />
              </div>
            </div>
            <div className="col-md-6 text-center">
              <img
                src={
                  template?.file?.startsWith("http")
                    ? template.file
                    : images.dummy.course
                }
                alt={template.title}
                style={{ maxHeight: "360px", borderRadius: "10px" }}
                className="img-fluid"
              />
            </div>
          </div>

          {yoututbeUrl && (
            <div className="video-container ratio ratio-16x9 mb-5">
              <iframe
                src={yoututbeUrl}
                title="Press Video"
                style={{ borderRadius: "12px" }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          )}

          {/* Info Sections */}
          <div className="row text-start mb-5">
            <div className="col-md-6">
              <h5 className="fw-bold mb-2">{t("creatorBenefits")}</h5>
              <div
                dangerouslySetInnerHTML={{
                  __html: template.creator_benefits || "",
                }}
              />
            </div>
            <div className="col-md-6">
              <h5 className="fw-bold mb-2">{t("whyBuildTrack")}</h5>
              <div
                dangerouslySetInnerHTML={{
                  __html: template.motivation || "",
                }}
              />
            </div>
          </div>

          {/* Problem / Solution / Proof */}
          <div className="row mb-4">
            <div className="col-md-4 mt-2">
              <div
                className="p-3 h-100"
                style={{ backgroundColor: "#17161b", borderRadius: "10px" }}
              >
                <h6 className="fw-bold mb-2">{t("problem")}</h6>
                <p
                  className="small mb-0"
                  dangerouslySetInnerHTML={{ __html: template.problem || "" }}
                />
              </div>
            </div>
            <div className="col-md-4 mt-2">
              <div
                className="p-3 h-100"
                style={{ backgroundColor: "#17161b", borderRadius: "10px" }}
              >
                <h6 className="fw-bold mb-2">{t("whyCreatorsLove")}</h6>
                <p
                  className="small mb-0"
                  dangerouslySetInnerHTML={{
                    __html: template.creator_template || "",
                  }}
                />
              </div>
            </div>
            <div className="col-md-4 mt-2">
              <div
                className="p-3 h-100"
                style={{ backgroundColor: "#17161b", borderRadius: "10px" }}
              >
                <h6 className="fw-bold mb-2">
                  {template.socialproof || t("usedBy")}
                </h6>
                <p
                  className="small mb-0"
                  dangerouslySetInnerHTML={{ __html: template.hook || "" }}
                />
              </div>
            </div>
          </div>

          {/* QR Code */}
          <div className="row mb-4 justify-content-center">
            <div className="col-md-12 text-center">
              <QRCodeCanvas
                id="template-qr"
                value={currentUrl}
                size={150}
                bgColor={"#ffffff"}
                fgColor={"#000000"}
                level={"H"}
                includeMargin={true}
                style={{ borderRadius: "10px" }}
              />
              <p className="mt-2 small text-muted">{t("scanCamera")}</p>
              <button
                className="site-btn mt-2"
                onClick={() => {
                  const canvas = document.getElementById("template-qr");
                  const pngUrl = canvas
                    .toDataURL("image/png")
                    .replace("image/png", "image/octet-stream");
                  const downloadLink = document.createElement("a");
                  downloadLink.href = pngUrl;
                  downloadLink.download = `template-${slug}-qr.png`;
                  document.body.appendChild(downloadLink);
                  downloadLink.click();
                  document.body.removeChild(downloadLink);
                }}
              >
                {t("downloadQR")}
              </button>
            </div>
          </div>

          {/* Action Button */}
          <div className="row mb-5 justify-content-center">
            <div
              className="col-md-12"
              style={{ justifyItems: "center", width: "fit-content" }}
            >
              <button
                className="site-btn w-100 mt-2"
                onClick={handleAccessClick}
              >
                {template.access_type === "paid"
                  ? isProductPurchased
                    ? t("active")
                    : `${t("buyBundle")} - ${displayPrice ?? <Spinner />}`
                  : template.access_type === "free"
                  ? t("startFree")
                  : template.access_type === "membership"
                  ? t("useMembership")
                  : template.access_type === "custom"
                  ? t("requestCustom")
                  : t("useTemplate")}
              </button>
            </div>
          </div>

          {/* Supporting Files */}
          {template.supportingfiles && (
            <div className="row mb-5">
              <div className="col-md-12" style={{ justifyItems: "center" }}>
                <div
                  className="p-3 text-center"
                  style={{
                    backgroundColor: "#2b2b2b",
                    borderRadius: "10px",
                    width: "fit-content",
                  }}
                >
                  <h6 className="fw-bold mb-2">{t("supportingFiles")}</h6>
                  <a
                    href={template.supportingfiles}
                    className="site-btn"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("downloadPDF")}
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="text-center">
            <p className="mt-3 text-muted" style={{ fontSize: "13px" }}>
              {t("needHelp")}{" "}
              <a href="/contact-us" className="text-light">
                {t("contactUs")}
              </a>
            </p>
          </div>
        </div>
      </section>
    </TemplateLayout>
  );
};

export default TemplateDetail;
